// MAIN positions with protective-stop status (2026-10-04, GUI audit docs/GUI_REDESIGN_AUDIT_2026_10_04.md).
// No page showed a MAIN position before; a position with no stop (best practices 1.4) was visible only as an alert.
// Data: GET /api/main/positions (broker positions + open stop orders, cached 10 s server side). Polls every 15 s
// while this page is mounted AND the tab is visible, so a hidden tab costs nothing (best practices 2.3: shared
// connection ceiling). Unprotected rows sort first and are always red.
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { RefreshCw, ShieldAlert, ShieldCheck } from 'lucide-react'
import { api } from '../lib/api'
import { timeAgo } from '../lib/utils'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const COLS = [
  { key: 'symbol', label: 'Symbol', align: 'left' },
  { key: 'qty', label: 'Qty' },
  { key: 'avg_entry_price', label: 'Entry' },
  { key: 'current_price', label: 'Last' },
  { key: 'market_value', label: 'Value' },
  { key: 'unrealized_pl', label: 'P&L $' },
  { key: 'pnl_pct', label: 'P&L %' },
  { key: 'has_stop', label: 'Stop' },
  { key: 'stop_price', label: 'Stop px' },
  { key: 'held_days', label: 'Held d' },
]
const num = (v, d = 2) => (v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }))
const signed = (v, d = 2) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${num(v, d)}`)
const CELL = { padding: '3px 10px', textAlign: 'right' }

export default function Positions() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [asOf, setAsOf] = useState(null)
  const [sort, setSort] = useState({ key: null, dir: 'asc' })   // null = server order: unprotected first
  const [filter, setFilter] = useState(() => (new URLSearchParams(window.location.search).get('s') || '').toUpperCase())

  const load = useCallback(async () => {
    try {
      const res = await api.mainPositions()
      if (res.success) { setData(res); setAsOf(res.as_of); setError(null) } else setError(res.error || 'request failed')
    } catch (e) { setError(String(e.message || e)) }
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, 15_000)
    return () => clearInterval(id)
  }, [load])

  const rows = useMemo(() => {
    let r = (data?.positions || []).filter(p => !filter || p.symbol.includes(filter.toUpperCase()))
    if (sort.key) {
      r = [...r].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key]
        if (av == null) return 1
        if (bv == null) return -1
        const c = typeof av === 'string' ? av.localeCompare(bv) : (av === bv ? 0 : (av < bv ? -1 : 1))
        return sort.dir === 'asc' ? c : -c
      })
    }
    return r
  }, [data, sort, filter])

  const s = data?.summary
  const toggle = (key) => setSort(p => p.key === key ? (p.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' }) : { key, dir: 'asc' })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-slate-200 font-semibold tracking-widest" style={{ ...MONO, fontSize: '0.8rem' }}>MAIN POSITIONS</h2>
        {s && <span className="text-slate-500" style={{ ...MONO, fontSize: '0.7rem' }}>{s.count} open · ${num(s.market_value, 0)} · unrealized {signed(s.unrealized_pl)}</span>}
        <span className="flex-1" />
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="symbol"
          className="px-2 py-1 rounded border border-gray-800 bg-transparent text-slate-300" style={{ ...MONO, fontSize: '0.7rem', width: 110 }} />
        <button onClick={load} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-200 border border-gray-800 rounded px-2.5 py-1" style={{ ...MONO, fontSize: '0.68rem' }}>
          <RefreshCw className="h-3 w-3" /> REFRESH
        </button>
        <span className="text-slate-600" style={{ ...MONO, fontSize: '0.65rem' }}>{asOf ? `as of ${timeAgo(asOf)} · auto 15s` : 'loading…'}</span>
      </div>

      {error && (
        <div className="rounded border px-3 py-2" style={{ ...MONO, fontSize: '0.72rem', color: '#fda4af', borderColor: '#f43f5e', background: 'rgba(244,63,94,0.08)' }}>
          Could not load MAIN positions: {error}. The table below, if any, is the last good copy.
        </div>
      )}

      {s && s.unprotected_count > 0 && (
        <div className="rounded border px-3 py-2 flex items-center gap-2" style={{ ...MONO, fontSize: '0.78rem', color: '#fecaca', borderColor: '#ef4444', background: 'rgba(127,29,29,0.45)' }}>
          <ShieldAlert className="h-4 w-4" /> {s.unprotected_count} position{s.unprotected_count === 1 ? '' : 's'} with NO protective stop: <b>{s.unprotected.join(', ')}</b>
        </div>
      )}
      {s && s.unprotected_count === 0 && (
        <div className="rounded border px-3 py-2 flex items-center gap-2" style={{ ...MONO, fontSize: '0.74rem', color: '#6ee7b7', borderColor: '#10b981', background: 'rgba(6,78,59,0.25)' }}>
          <ShieldCheck className="h-4 w-4" /> every open position has a live stop order
        </div>
      )}

      <div className="rounded-lg border border-gray-800 overflow-auto" style={{ background: '#111827', maxHeight: 'calc(100vh - 260px)' }}>
        <table className="w-full" style={{ ...MONO, fontSize: '0.74rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {COLS.map(c => (
                <th key={c.key} onClick={() => toggle(c.key)} className="cursor-pointer select-none"
                  style={{ padding: '6px 10px', textAlign: c.align || 'right', color: '#64748b', fontWeight: 600, fontSize: '0.62rem', letterSpacing: '0.06em', position: 'sticky', top: 0, background: '#111827', borderBottom: '1px solid #1e293b' }}>
                  {c.label.toUpperCase()}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.symbol} style={{ background: p.has_stop ? 'transparent' : 'rgba(127,29,29,0.35)', borderTop: '1px solid rgba(30,41,59,0.7)' }}>
                <td style={{ ...CELL, textAlign: 'left', color: '#e2e8f0', fontWeight: 600 }}>{p.symbol}</td>
                <td style={CELL}>{num(p.qty, 0)}</td>
                <td style={CELL}>{num(p.avg_entry_price)}</td>
                <td style={CELL}>{num(p.current_price)}</td>
                <td style={CELL}>{num(p.market_value, 0)}</td>
                <td style={{ ...CELL, color: (p.unrealized_pl ?? 0) >= 0 ? '#34d399' : '#fb7185' }}>{signed(p.unrealized_pl)}</td>
                <td style={{ ...CELL, color: (p.pnl_pct ?? 0) >= 0 ? '#34d399' : '#fb7185' }}>{signed(p.pnl_pct)}</td>
                <td style={{ ...CELL, color: p.has_stop ? '#34d399' : '#fca5a5', fontWeight: 700 }}>{p.has_stop ? '✓' : 'NO STOP'}</td>
                <td style={CELL}>{p.stop_price ? num(p.stop_price) : '—'}</td>
                <td style={CELL}>{p.held_days == null ? '—' : p.held_days.toFixed(1)}</td>
              </tr>
            ))}
            {data && rows.length === 0 && (
              <tr><td colSpan={COLS.length} style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>{filter ? 'no match' : 'no open MAIN positions'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-slate-600" style={{ ...MONO, fontSize: '0.64rem' }}>
        Source: broker positions and open orders (a stop exists only if Alpaca lists a live open stop-type sell order); entry age from position_tracking. P&amp;L here is unrealized, per position, not a strategy result.
      </p>
    </div>
  )
}
